// frontend/app/demoPreset.js
import { getFeatureFlags } from "./features";
const preset = require("./demo_preset.json");

// ---- feature toggle helpers ----
export function isInvestorOn() {
  const flags = getFeatureFlags();
  return !!flags.investor_mode;
}
export function getDemoPreset() {
  return isInvestorOn() ? preset : null;
}

// ---- utils ----
function norm(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ") // strip punctuation (incl. en/em dashes), keep letters/numbers
    .trim()
    .replace(/\s+/g, " ");            // collapse spaces
}
function looseMatch(userQ, targetQ) {
  const a = norm(userQ);
  const b = norm(targetQ);
  if (!a || !b) return false;
  // exact, startsWith either way, or substring either way
  return a === b || a.startsWith(b) || b.startsWith(a) || a.includes(b) || b.includes(a);
}

// ---- topic lookup ----
export function findTopicBySourceTopicId(source_topic_id) {
  return (preset.topics || []).find(
    t => String(t.source_topic_id) === String(source_topic_id)
  ) || null;
}

// ---- legacy (gold_prompts) matcher (loose) ----
function findLegacyFlowForQuestionLoose(question, topicOpt) {
  const topics = topicOpt ? [topicOpt] : (preset.topics || []);
  for (const tp of topics) {
    const gps = tp.gold_prompts || [];
    for (const g of gps) {
      if (looseMatch(question, g)) {
        // map to flow shape (don’t spread whole topic)
        return { topic: tp, flow: { ask: g, pwo: tp.pwo, video: tp.video, quiz: tp.quiz } };
      }
    }
  }
  return null;
}

// ---- flows[] matcher (loose) ----
function findFlowInTopicLoose(tp, question) {
  const flows = Array.isArray(tp?.flows) ? tp.flows : [];
  for (const f of flows) {
    if (looseMatch(question, f.ask)) return f;
  }
  return null;
}

// ---- main: try topic-scoped flows → topic-scoped legacy → global flows → global legacy ----
export function findFlowOrLegacyForQuestion(source_topic_id, question) {
  if (!isInvestorOn()) return null;

  const topic = source_topic_id ? findTopicBySourceTopicId(source_topic_id) : null;

  // 1) Prefer flows within the known topic
  if (topic) {
    const flow = findFlowInTopicLoose(topic, question);
    if (flow) return { topic, flow };
  }

  // 2) Then legacy gold_prompts within the known topic
  if (topic) {
    const legacy = findLegacyFlowForQuestionLoose(question, topic);
    if (legacy) return legacy;
  }

  // 3) Otherwise, scan all topics for flows
  for (const tp of (preset.topics || [])) {
    const flow = findFlowInTopicLoose(tp, question);
    if (flow) return { topic: tp, flow };
  }

  // 4) Finally, scan all topics for legacy gold_prompts
  const legacyGlobal = findLegacyFlowForQuestionLoose(question, null);
  if (legacyGlobal) return legacyGlobal;

  return null;
}

// ---- alias so older calls keep working ----
export const findFlowForQuestion = (source_topic_id, q) =>
  findFlowOrLegacyForQuestion(source_topic_id, q);

// BDD 10/12/2025
// Collect all unique demo videos from the preset (flows first, then legacy top-level)
export function listDemoVideos(presetOpt) {
  const p = presetOpt || getDemoPreset();
  const out = [];
  const seen = new Set();
  if (!p?.topics) return out;

  for (const tp of p.topics) {
    const flows = Array.isArray(tp?.flows) ? tp.flows : [];
    for (const f of flows) {
      const v = f?.video || {};
      const key =
        v?.video_id != null ? `${v.video_id}-${v.tag_id ?? ""}` :
        v?.uuid ? `uuid:${v.uuid}-${v.version ?? ""}` : null;
      if (!key || seen.has(key)) continue;

      out.push({
        videoID: v.video_id ?? null,
        tagID: v.tag_id ?? null,
        uuid: v.uuid ?? null,
        version: v.version ?? null,
      });
      seen.add(key);
    }

    // Fallback if a topic uses legacy top-level video
    if (tp?.video) {
      const v = tp.video;
      const key =
        v?.video_id != null ? `${v.video_id}-${v.tag_id ?? ""}` :
        v?.uuid ? `uuid:${v.uuid}-${v.version ?? ""}` : null;
      if (key && !seen.has(key)) {
        out.push({
          videoID: v.video_id ?? null,
          tagID: v.tag_id ?? null,
          uuid: v.uuid ?? null,
          version: v.version ?? null,
        });
        seen.add(key);
      }
    }
  }
  return out;
}
// ==== Trivia helpers (demo only) ====
export function getTriviaForTopic(source_topic_id = null, presetOpt) {
  const p = presetOpt || getDemoPreset();
  if (!p?.topics) return [];

  const topics = source_topic_id
    ? (p.topics || []).filter(t => String(t.source_topic_id) === String(source_topic_id))
    : (p.topics || []);

  const out = [];
  const seen = new Set();

  for (const tp of topics) {
    // Prefer topic-level trivia
    const arr = Array.isArray(tp?.trivia) ? tp.trivia : [];
    for (const raw of arr) {
      const id = raw?.id || `${tp.source_topic_id}|${raw?.q}`; // fallback id
      if (seen.has(id)) continue;
      out.push(normalizeTrivia(raw));
      seen.add(id);
    }
    // Optionally merge flows[].quiz.questions if you ever populate them later
    const flows = Array.isArray(tp?.flows) ? tp.flows : [];
    for (const f of flows) {
      const qs = Array.isArray(f?.quiz?.questions) ? f.quiz.questions : [];
      for (const raw of qs) {
        const id = raw?.id || `${tp.source_topic_id}|${raw?.q}`;
        if (seen.has(id)) continue;
        out.push(normalizeTrivia(raw));
        seen.add(id);
      }
    }
  }
  return out;
}

function normalizeTrivia(r) {
  // Keep a minimal shape for the waiting-card renderer
  if (!r) return null;
  const type = r.type === "tf" ? "tf" : "mcq";
  return {
    id: r.id || String(Math.random()).slice(2),
    type,
    q: r.q || "",
    options: Array.isArray(r.options) ? r.options : [],
    answer_index: typeof r.answer_index === "number" ? r.answer_index : null,
    answer_bool: typeof r.answer_bool === "boolean" ? r.answer_bool : null,
    explain: r.explain || ""
  };
}
